import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, ShieldCheck, User as UserIcon, Check, X } from 'lucide-react';
import { authApi } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { useGlobalPrefs, useGlobalPrefsUpdate } from '@/lib/myPrefs';
import clsx from 'clsx';

/**
 * Small identity pill rendered in the topbar.
 *
 * Phase 2.3: identity is the *browser* (auto-issued `c_<hex>` cookie),
 * not a typed username. The pill shows either:
 *   - the legacy username when an older session is still active, or
 *   - a friendly display name (B1 prefs.global.ui.displayName，跟账号走、
 *     跨电脑同步)，未设置时默认 "终端-<6 hex prefix>"。
 *
 * Click the pencil to rename; admin (token-mode) sessions still get the
 * shield badge. There's no "logout" anymore — the cookie is the
 * browser's identity and clearing it would silently mint a fresh
 * orphan id, losing all owned devices. If a user *really* wants a
 * new identity they can clear site data manually.
 */
export function UserBadge() {
  const qc = useQueryClient();
  const user = useAuth((s) => s.user);
  const storedName = useGlobalPrefs((g) => g.ui.displayName);
  const updatePrefs = useGlobalPrefsUpdate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // Token-mode "admin" sessions and any rare legacy username login keep
  // the old behaviour — show that name verbatim and let users sign out
  // back into auto-issued mode by clearing the legacy cookie.
  const logout = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['auth.me'] });
    },
  });

  if (!user) return null;

  const isAdmin = user.role === 'admin';
  const isAutoId = /^c_[0-9a-f]{22}$/.test(user.username);
  const Icon = isAdmin ? ShieldCheck : UserIcon;

  const shortId = user.username.slice(2, 8); // "abc123" from "c_abc123…"
  const fallbackDisplay = isAutoId ? `终端-${shortId}` : user.username;
  const display = (storedName || '').trim() || fallbackDisplay;

  const saveDisplayName = (): void => {
    const v = draft.trim();
    if (v.length > 32) return;
    updatePrefs({ ui: { displayName: v } });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Icon className="h-3.5 w-3.5 text-ink-muted" />
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveDisplayName();
            else if (e.key === 'Escape') setEditing(false);
          }}
          placeholder={fallbackDisplay}
          maxLength={32}
          className="h-7 rounded-full border border-line bg-bg-card px-2.5 text-xs"
        />
        <button className="btn-ghost !px-1.5" onClick={saveDisplayName} title="保存">
          <Check className="h-3.5 w-3.5" />
        </button>
        <button className="btn-ghost !px-1.5" onClick={() => setEditing(false)} title="取消">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <span
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
          isAdmin
            ? 'border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-400'
            : 'border-line bg-bg-soft text-ink-muted'
        )}
        title={
          isAdmin
            ? '系统管理员（通过 token 登录）'
            : `${display}\n本机 ID: ${user.username}\n显示名跟随账号，登录任何浏览器都看到，点笔图标修改`
        }
      >
        <Icon className="h-3.5 w-3.5" />
        <span>{display}</span>
        {isAdmin && <span className="text-[10px] uppercase tracking-wide">admin</span>}
      </span>
      {isAutoId && (
        <button
          className="btn-ghost !px-1.5"
          onClick={() => {
            setDraft(storedName);
            setEditing(true);
          }}
          title="设置显示名（跟随账号，所有设备同步）"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
      {!isAutoId && (
        <button
          className="btn-ghost !px-2"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          title="清除遗留登录 cookie；下次请求会自动分配本机 ID"
        >
          <span className="text-[10px]">{logout.isPending ? '清除中…' : '清除登录'}</span>
        </button>
      )}
    </div>
  );
}
